// =============================================================================
// PedidoDetalhe — o orçamento aberto.
//
// Mostra o que foi vendido, para quem, por quem e o que aconteceu com o pedido
// desde então. O histórico vem do LEDGER (fnx_pedido_evento), que é a fonte da
// verdade do estado — não um campo que alguém pode ter sobrescrito.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api, { mensagemDeErro } from '../services/api';
import DocumentoOrcamento from '../components/DocumentoOrcamento';

const dinheiro = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const numero = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 4 });
const dataHora = (v) => (v ? String(v).slice(0, 16).replace('T', ' ') : '—');

const ESTADOS = {
  RASCUNHO: { rotulo: 'Rascunho', classe: 'badge-neutro' },
  AGUARDANDO_LIBERACAO: { rotulo: 'Aguardando liberação', classe: 'badge-pendente' },
  CONFIRMADO: { rotulo: 'Confirmado', classe: 'badge-pendente' },
  AGUARDANDO_INTEGRACAO: { rotulo: 'Aguardando integração', classe: 'badge-pendente' },
  INTEGRANDO: { rotulo: 'Enviando', classe: 'badge-pendente' },
  INTEGRADO: { rotulo: 'No Microuniverso', classe: 'badge-positivo' },
  DIVERGENTE: { rotulo: 'Sem resposta do ERP', classe: 'badge-negativo' },
  ERRO_INTEGRACAO: { rotulo: 'Erro na integração', classe: 'badge-negativo' },
  CANCELADO: { rotulo: 'Cancelado', classe: 'badge-neutro' },
};

const ENTREGA = {
  // Os nomes do Microuniverso — é o que a loja fala.
  C: { rotulo: 'Caixa', icone: 'bi-bag-check' },
  E: { rotulo: 'Expedição', icone: 'bi-shop' },
  F: { rotulo: 'Futura', icone: 'bi-truck' },
};

export default function PedidoDetalhe() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  // Duas leituras do mesmo pedido: a de trabalho (o que o Fênix sabe) e a do
  // documento (o papel que a loja conhece). Quem opera usa a primeira; quem
  // entrega ao cliente usa a segunda.
  const [aba, setAba] = useState('resumo');

  const carregar = useCallback(() => {
    api.get(`/api/pedidos/${uuid}`)
      .then((r) => setDados(r.data))
      .catch((e) => setErro(mensagemDeErro(e)));
  }, [uuid]);

  useEffect(() => { carregar(); }, [carregar]);

  // O pedido que ficou sem resposta do Microuniverso: procura a marca do Fênix
  // lá. Só procura — mandar de novo poderia duplicar o orçamento no ERP.
  async function procurarDeNovo() {
    try {
      const { data } = await api.post(`/api/pedidos/${uuid}/procurar`);
      await Swal.fire(data.numped_erp
        ? { icon: 'success', title: `Orçamento ${data.numped_erp}`, html: `Achado no Microuniverso com o número <strong>${data.numped_erp}</strong>.`, confirmButtonColor: '#EA6A08' }
        : { icon: 'info', title: 'Ainda não apareceu', text: 'O orçamento não está no Microuniverso. Confira lá antes de refazer, e fale com a TI.', confirmButtonColor: '#EA6A08' });
      carregar();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Não foi possível procurar', text: mensagemDeErro(e) });
    }
  }

  if (erro) {
    return (
      <>
        <button className="btn btn-link mb-3" onClick={() => navigate('/pedidos')}>
          <i className="bi bi-arrow-left me-1" />Voltar
        </button>
        <div className="alert alert-danger py-2 small">{erro}</div>
      </>
    );
  }
  if (!dados) {
    return <div className="text-center py-5 text-muted"><span className="spinner-border spinner-border-sm me-2" />Carregando…</div>;
  }

  const { pedido, itens, eventos, liberacoes } = dados;
  const est = ESTADOS[pedido.status] || ESTADOS.RASCUNHO;

  return (
    <>
      <button className="btn btn-link mb-2 nao-imprimir" onClick={() => navigate('/pedidos')}>
        <i className="bi bi-arrow-left me-1" />Pedidos
      </button>

      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3 nao-imprimir">
        <div>
          <h1 className="h4 fw-bold mb-1">
            {pedido.numero}
            <span className={`badge ${est.classe} ms-2`} style={{ fontSize: '.6em', verticalAlign: 'middle' }}>
              {est.rotulo}
            </span>
          </h1>
          <p className="text-muted small mb-0">
            {pedido.cliente_razao || 'Sem cliente'} · filial {pedido.filial_venda}
            {pedido.filial_nome ? ` (${pedido.filial_nome})` : ''}
          </p>
        </div>
        <div className="text-end">
          <div className="h4 fw-bold mb-0" style={{ color: 'var(--primary)' }}>{dinheiro(pedido.valor_liquido)}</div>
          {pedido.numped_erp
            ? <div className="small text-muted">nº {pedido.numped_erp} no Microuniverso</div>
            : <div className="small text-muted">ainda não integrado</div>}
        </div>
      </div>

      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3 nao-imprimir">
        <ul className="nav nav-tabs" style={{ borderBottom: 'none' }}>
          <li className="nav-item">
            <button className={`nav-link ${aba === 'resumo' ? 'active' : ''}`}
                    onClick={() => setAba('resumo')}>
              <i className="bi bi-list-ul me-1" />Resumo
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link ${aba === 'documento' ? 'active' : ''}`}
                    onClick={() => setAba('documento')}>
              <i className="bi bi-file-earmark-text me-1" />Documento
            </button>
          </li>
        </ul>
        {aba === 'documento' && (
          <button className="btn btn-sm btn-outline-fnx" onClick={() => window.print()}>
            <i className="bi bi-printer me-1" />Imprimir
          </button>
        )}
      </div>

      {aba === 'documento' && (
        <div className="card">
          <div className="card-body p-0">
            <DocumentoOrcamento pedido={pedido} itens={itens} />
          </div>
        </div>
      )}

      {aba === 'resumo' && <>
      {['INTEGRANDO', 'DIVERGENTE'].includes(pedido.status) && (
        <div className="alert alert-light border d-flex align-items-center justify-content-between flex-wrap gap-2 py-2">
          <div className="small">
            <i className="bi bi-question-circle me-2" />
            O Microuniverso não confirmou este orçamento. Ele pode ter chegado mesmo assim.
          </div>
          <button className="btn btn-sm btn-outline-fnx" onClick={procurarDeNovo}>
            Procurar de novo no Microuniverso
          </button>
        </div>
      )}

      {pedido.integracao_manual === 1 && (
        <div className="alert alert-light border py-2 small">
          <i className="bi bi-keyboard me-2" />
          Este pedido foi <strong>digitado manualmente</strong> no Microuniverso
          {pedido.integrado_por_nome ? ` por ${pedido.integrado_por_nome}` : ''}
          {pedido.integrado_em ? ` em ${dataHora(pedido.integrado_em)}` : ''}.
        </div>
      )}

      {pedido.erp_indisponivel === 1 && (
        <div className="alert alert-warning py-2 small">
          <i className="bi bi-shield-exclamation me-2" />
          Este pedido nasceu com o Microuniverso indisponível.
        </div>
      )}

      <div className="row g-3">
        <div className="col-12 col-lg-8">
          <div className="card mb-3">
            <div className="card-body">
              <h2 className="h6 fw-bold mb-3">Itens</h2>
              {itens.map((i) => {
                const e = ENTREGA[i.tipo_entrega] || ENTREGA.C;
                return (
                  <div key={i.sequencia} className="border-bottom py-2">
                    <div className="d-flex justify-content-between gap-2">
                      <div className="min-width-0">
                        <div className="fw-semibold small">{i.descricao}</div>
                        <div className="text-muted" style={{ fontSize: '.76rem' }}>
                          cód. {i.codpro} · {numero(i.quantidade)} {i.unidade} × {dinheiro(i.preco_unitario)}
                        </div>
                        <div className="text-muted mt-1" style={{ fontSize: '.76rem' }}>
                          <i className={`bi ${e.icone} me-1`} />{e.rotulo}
                          {i.filial_estoque && i.filial_estoque !== pedido.filial_venda && (
                            <> · sai da filial <strong>{i.filial_estoque}</strong></>
                          )}
                          {i.aguarda_transferencia === 1 && (
                            <span className="badge badge-pendente ms-2">aguarda transferência</span>
                          )}
                          {i.dt_prev_ent && <> · previsão {String(i.dt_prev_ent).slice(0, 10)}</>}
                          {i.sem_saldo_confirmado === 1 && (
                            <span className="badge badge-negativo ms-2">vendido sem saldo</span>
                          )}
                        </div>
                      </div>
                      <div className="text-end" style={{ whiteSpace: 'nowrap' }}>
                        <div className="fw-bold">{dinheiro(i.valor_liquido)}</div>
                        {Number(i.desconto_rateado) > 0 && (
                          <div className="text-muted" style={{ fontSize: '.74rem' }}>
                            − {dinheiro(i.desconto_rateado)} rateado
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="d-flex justify-content-between small text-muted mt-3">
                <span>Bruto</span><span>{dinheiro(pedido.valor_bruto)}</span>
              </div>
              {Number(pedido.desconto_cabecalho) > 0 && (
                <div className="d-flex justify-content-between small text-muted">
                  <span>Desconto no pedido</span><span>− {dinheiro(pedido.desconto_cabecalho)}</span>
                </div>
              )}
              <div className="d-flex justify-content-between fw-bold mt-1">
                <span>Total</span><span>{dinheiro(pedido.valor_liquido)}</span>
              </div>
            </div>
          </div>

          {liberacoes?.length > 0 && (
            <div className="card mb-3">
              <div className="card-body">
                <h2 className="h6 fw-bold mb-2">Liberações</h2>
                {liberacoes.map((l) => (
                  <div key={l.id} className="small border-bottom py-2">
                    <span className={`badge ${l.status === 'APROVADA' ? 'badge-positivo' : l.status === 'RECUSADA' ? 'badge-negativo' : 'badge-pendente'} me-2`}>
                      {l.status}
                    </span>
                    <strong>{l.tipo}</strong> — {l.motivo}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="col-12 col-lg-4">
          <div className="card mb-3">
            <div className="card-body">
              <h2 className="h6 fw-bold mb-3">Dados</h2>
              <dl className="mb-0" style={{ fontSize: '.85rem' }}>
                <dt className="text-muted fw-normal">Vendedor</dt>
                <dd className="mb-2">{pedido.vendedor_nome || pedido.codvend || '—'}</dd>
                <dt className="text-muted fw-normal">Lançado por</dt>
                <dd className="mb-2">{pedido.criado_por_nome || '—'}</dd>
                <dt className="text-muted fw-normal">Pagamento</dt>
                <dd className="mb-2">
                  {pedido.plano_pagamento_nome || pedido.plano_pagamento || '—'}
                  {pedido.tipo_documento_nome ? ` · ${pedido.tipo_documento_nome}` : ''}
                </dd>
                <dt className="text-muted fw-normal">Tipo de pedido</dt>
                <dd className="mb-2">{pedido.tpo_nome || pedido.tpo || '—'}</dd>
                <dt className="text-muted fw-normal">Criado em</dt>
                <dd className="mb-2">{dataHora(pedido.criado_em)}</dd>
                {pedido.observacao && (<>
                  <dt className="text-muted fw-normal">Observação</dt>
                  <dd className="mb-0">{pedido.observacao}</dd>
                </>)}
              </dl>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h2 className="h6 fw-bold mb-3">Histórico</h2>
              {/* Vem do ledger append-only: é o que de fato aconteceu, na ordem
                  em que aconteceu. */}
              {eventos.map((ev, i) => (
                <div key={i} className="d-flex gap-2 pb-3" style={{ fontSize: '.82rem' }}>
                  <div style={{ color: 'var(--primary)' }}><i className="bi bi-circle-fill" style={{ fontSize: '.5rem' }} /></div>
                  <div className="min-width-0">
                    <div className="fw-semibold">{ev.evento}</div>
                    {ev.mensagem && <div className="text-muted">{ev.mensagem}</div>}
                    <div className="text-muted" style={{ fontSize: '.74rem' }}>
                      {dataHora(ev.criado_em)} · {ev.autor_nome || ev.autor_tipo}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      </>}
    </>
  );
}
